import React, { useState, useEffect } from 'react';
import { Activity, Calendar, TrendingUp, Award, Users, BookOpen, Plane, Sunrise, Star, Sparkles, MapPin, Clock, User, Play, ChevronDown, ChevronUp } from 'lucide-react';
import CosmicBackground from '../components/CosmicBackground';

const KernelDisplay = () => {
  // Form state
  const [formData, setFormData] = useState({
    name: 'Mandeep',
    gender: 'Male',
    date: '1997-05-14',
    time: '12:17',
    location: 'Dehradun, Uttarakhand, India',
    latitude: 30.3255646,
    longitude: 78.0436813,
    timezone: 'Asia/Kolkata'
  });
  
  const [kernelData, setKernelData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(true);
  const [showVideo, setShowVideo] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLocationChange = (e) => {
    const location = e.target.value;
    setFormData(prev => ({ ...prev, location }));
    
    // Mock geocoding - in real app, use Google Places API
    if (location.toLowerCase().includes('dehradun')) {
      setFormData(prev => ({
        ...prev,
        latitude: 30.3255646,
        longitude: 78.0436813,
        timezone: 'Asia/Kolkata'
      }));
    } else if (location.toLowerCase().includes('delhi')) {
      setFormData(prev => ({
        ...prev,
        latitude: 28.6139,
        longitude: 77.2090,
        timezone: 'Asia/Kolkata'
      }));
    } else if (location.toLowerCase().includes('mumbai')) {
      setFormData(prev => ({
        ...prev,
        latitude: 19.0760,
        longitude: 72.8777,
        timezone: 'Asia/Kolkata'
      }));
    }
  };

  const fetchKernelData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/kernel/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          person_id: formData.name.toLowerCase().replace(/\s+/g, '_'),
          date: formData.date,
          time: formData.time + ':00',
          latitude: formData.latitude,
          longitude: formData.longitude,
          timezone: formData.timezone,
          place_name: formData.location
        })
      });

      const data = await response.json();
      if (data.status === 'success') {
        setKernelData(data.kernel);
        setShowForm(false);
        setShowVideo(true);
      } else {
        setError('Failed to generate chart');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateChart = (e) => {
    e.preventDefault();
    fetchKernelData();
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  return (
    <CosmicBackground showAstroChart={false}>
      <div className="min-h-screen pb-24 px-4 py-8">
        
        {/* Header */}
        <div className="text-center mb-12">
          <img 
            src="/taraanaya-logo-transparent.png" 
            alt="Taraanaya" 
            className="h-16 mx-auto mb-4 object-contain"
            style={{ filter: 'brightness(1.2) saturate(1.1)' }}
          />
          <h1 className="text-white text-4xl font-light tracking-wider mb-2">
            🔮 Taraanaya
          </h1>
          <p className="text-cyan-400 text-lg">Royal, fast, and explainable Vedic insights.</p>
        </div>

        {/* Birth Details Input Form */}
        {showForm && (
          <div className="max-w-2xl mx-auto mb-12">
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
              <form onSubmit={handleGenerateChart} className="space-y-6">
                
                {/* Name and Gender Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="flex items-center gap-2 text-white/80 text-sm mb-2">
                      <User className="w-4 h-4" />
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-cyan-500 transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-white/80 text-sm mb-2 block">Gender</label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:border-cyan-500 transition-colors"
                    >
                      <option value="Male" className="bg-gray-900">Male</option>
                      <option value="Female" className="bg-gray-900">Female</option>
                      <option value="Other" className="bg-gray-900">Other</option>
                    </select>
                  </div>
                </div>

                {/* Date and Time Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="flex items-center gap-2 text-white/80 text-sm mb-2">
                      <Calendar className="w-4 h-4" />
                      Date
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:border-cyan-500 transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-white/80 text-sm mb-2">
                      <Clock className="w-4 h-4" />
                      Time
                    </label>
                    <input
                      type="time"
                      name="time"
                      value={formData.time}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:border-cyan-500 transition-colors"
                      required
                    />
                  </div>
                </div>

                {/* Location */}
                <div>
                  <label className="flex items-center gap-2 text-white/80 text-sm mb-2">
                    <MapPin className="w-4 h-4" />
                    Place
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleLocationChange}
                    placeholder="Enter city or location"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-cyan-500 transition-colors"
                    required
                  />
                  <p className="text-white/40 text-xs mt-2">
                    Timezone: {formData.timezone} • Lat: {formData.latitude.toFixed(4)} • Lon: {formData.longitude.toFixed(4)}
                  </p>
                </div>

                {/* Generate Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-medium text-lg hover:shadow-lg hover:shadow-cyan-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Generating Chart...' : 'Get Full Chart (Backend Dump)'}
                </button>

                {error && (
                  <div className="text-red-400 text-sm text-center mt-4 p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                    {error}
                  </div>
                )}
              </form>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="text-white text-2xl font-light animate-pulse">Generating your cosmic blueprint...</div>
          </div>
        )}

        {/* Video Section - Shows after chart generation */}
        {!showForm && showVideo && kernelData && (
          <div className="max-w-4xl mx-auto mb-12">
            <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-2xl p-6 border border-purple-500/20">
              <h2 className="text-white text-2xl font-light mb-4 flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-cyan-400" />
                Sage Vaarta - Your Divine Guidance
              </h2>
              
              {/* Video Player */}
              <div className="relative aspect-video bg-black/50 rounded-xl overflow-hidden">
                <img 
                  src="/sage-final.png"
                  alt="Sage"
                  className="w-full h-full object-contain opacity-60"
                  style={{ filter: 'brightness(0.8) contrast(1.1)' }}
                />
                
                {/* Play Button Overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <button className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-2xl hover:scale-110 transition-transform">
                    <Play className="w-10 h-10 text-white ml-1" fill="white" />
                  </button>
                </div>
              </div>
              
              <p className="text-white/60 text-sm mt-4 text-center">
                Click play to watch your personalized astrological insights
              </p>
              
              <button
                onClick={() => setShowVideo(false)}
                className="w-full mt-4 py-2 text-cyan-400 text-sm hover:text-cyan-300 transition-colors"
              >
                Continue to Full Chart →
              </button>
            </div>
          </div>
        )}

        {/* Chart Data Display - Dashboard Cards */}
        {!showForm && !showVideo && kernelData && (
          <div className="max-w-6xl mx-auto">
            
            {/* User Info Header */}
            <div className="text-center mb-12">
              <h2 className="text-white text-3xl font-light mb-2">{kernelData.input?.full_name || formData.name}</h2>
              <p className="text-cyan-400 text-lg">{kernelData.input?.date || formData.date} • {kernelData.input?.time || formData.time}</p>
              <p className="text-white/60 text-sm">{kernelData.input?.place || formData.location}</p>
            </div>

            {/* Full JSON Dump for ALL Sections */}
            <DashboardCard
              id="full_kernel_json"
              title="FULL KERNEL - Complete Astrological Data (All 13+ Sections)"
              icon={Star}
              expanded={expandedSections.full_kernel_json}
              onToggle={() => toggleSection('full_kernel_json')}
            >
              <div className="bg-black/30 rounded-xl p-4 overflow-auto max-h-[600px]">
                <pre className="text-green-400 text-xs font-mono whitespace-pre-wrap">
                  {JSON.stringify(kernelData, null, 2)}
                </pre>
              </div>
            </DashboardCard>
            
            {/* Birth Astronomical Data - Section 1.1 */}
            {kernelData["1.1_birth_astronomical_data"] && (
              <DashboardCard
                id="birth_astronomical"
                title="Section 1.1: Birth Astronomical Data"
                icon={Star}
                expanded={expandedSections.birth_astronomical}
                onToggle={() => toggleSection('birth_astronomical')}
              >
                <div className="bg-black/20 rounded-xl p-4">
                  <pre className="text-cyan-300 text-xs font-mono whitespace-pre-wrap">
                    {JSON.stringify(kernelData["1.1_birth_astronomical_data"], null, 2)}
                  </pre>
                </div>
              </DashboardCard>
            )}
            
            {/* D1 Rasi Per Planet - Section 1.2 */}
            {kernelData["1.2_d1_rasi_per_planet"] && (
              <DashboardCard
                id="d1_rasi"
                title="Section 1.2: D1 Rasi Per Planet"
                icon={Star}
                expanded={expandedSections.d1_rasi}
                onToggle={() => toggleSection('d1_rasi')}
              >
                <div className="bg-black/20 rounded-xl p-4 overflow-auto max-h-[500px]">
                  <pre className="text-yellow-300 text-xs font-mono whitespace-pre-wrap">
                    {JSON.stringify(kernelData["1.2_d1_rasi_per_planet"], null, 2)}
                  </pre>
                </div>
              </DashboardCard>
            )}

            {/* Varga System - Section 2 */}
            {kernelData.section_2_varga_system && (
              <DashboardCard
                id="varga_system"
                title="Section 2: Varga System (D2-D60 Divisional Charts)"
                icon={Activity}
                expanded={expandedSections.varga_system}
                onToggle={() => toggleSection('varga_system')}
              >
                <div className="bg-black/20 rounded-xl p-4 overflow-auto max-h-[500px]">
                  <pre className="text-purple-300 text-xs font-mono whitespace-pre-wrap">
                    {JSON.stringify(kernelData.section_2_varga_system, null, 2)}
                  </pre>
                </div>
              </DashboardCard>
            )}

            {/* Bhava Chalit - Section 3 */}
            {kernelData.section_3_bhava_chalit && (
              <DashboardCard
                id="bhava_chalit"
                title="Section 3: Bhava Chalit (House Cusps)"
                icon={Activity}
                expanded={expandedSections.bhava_chalit}
                onToggle={() => toggleSection('bhava_chalit')}
              >
                <div className="bg-black/20 rounded-xl p-4 overflow-auto max-h-[500px]">
                  <pre className="text-blue-300 text-xs font-mono whitespace-pre-wrap">
                    {JSON.stringify(kernelData.section_3_bhava_chalit, null, 2)}
                  </pre>
                </div>
              </DashboardCard>
            )}

            {/* Sudarshana Chakra - Section 4 */}
            {kernelData.section_4_sudarshana && (
              <DashboardCard
                id="sudarshana"
                title="Section 4: Sudarshana Chakra"
                icon={TrendingUp}
                expanded={expandedSections.sudarshana}
                onToggle={() => toggleSection('sudarshana')}
              >
                <div className="bg-black/20 rounded-xl p-4 overflow-auto max-h-[500px]">
                  <pre className="text-pink-300 text-xs font-mono whitespace-pre-wrap">
                    {JSON.stringify(kernelData.section_4_sudarshana, null, 2)}
                  </pre>
                </div>
              </DashboardCard>
            )}
            
            {/* Jaimini - Section 5 */}
            {kernelData.section_5_jaimini && (
              <DashboardCard
                id="jaimini"
                title="Section 5: Jaimini System (Chara Karakas)"
                icon={Award}
                expanded={expandedSections.jaimini}
                onToggle={() => toggleSection('jaimini')}
              >
                <div className="bg-black/20 rounded-xl p-4 overflow-auto max-h-[500px]">
                  <pre className="text-orange-300 text-xs font-mono whitespace-pre-wrap">
                    {JSON.stringify(kernelData.section_5_jaimini, null, 2)}
                  </pre>
                </div>
              </DashboardCard>
            )}
            
            {/* Special Lagnas - Section 7 */}
            {kernelData.section_7_special_lagnas_points && (
              <DashboardCard
                id="special_lagnas"
                title="Section 7: Special Lagnas & Points"
                icon={Sparkles}
                expanded={expandedSections.special_lagnas}
                onToggle={() => toggleSection('special_lagnas')}
              >
                <div className="bg-black/20 rounded-xl p-4 overflow-auto max-h-[500px]">
                  <pre className="text-indigo-300 text-xs font-mono whitespace-pre-wrap">
                    {JSON.stringify(kernelData.section_7_special_lagnas_points, null, 2)}
                  </pre>
                </div>
              </DashboardCard>
            )}
            
            {/* KP System - Section 10 */}
            {kernelData.section_10_kp_system && (
              <DashboardCard
                id="kp_system"
                title="Section 10: KP System (Cuspal Sub-lords)"
                icon={BookOpen}
                expanded={expandedSections.kp_system}
                onToggle={() => toggleSection('kp_system')}
              >
                <div className="bg-black/20 rounded-xl p-4 overflow-auto max-h-[500px]">
                  <pre className="text-teal-300 text-xs font-mono whitespace-pre-wrap">
                    {JSON.stringify(kernelData.section_10_kp_system, null, 2)}
                  </pre>
                </div>
              </DashboardCard>
            )}
            
            {/* Dasha Catalogue - Section 11 */}
            {kernelData.section_11_dasha_catalogue && (
              <DashboardCard
                id="dasha_catalogue"
                title="Section 11: Dasha Catalogue (Vimshottari & Yogini)"
                icon={Calendar}
                expanded={expandedSections.dasha_catalogue}
                onToggle={() => toggleSection('dasha_catalogue')}
              >
                <div className="bg-black/20 rounded-xl p-4 overflow-auto max-h-[500px]">
                  <pre className="text-lime-300 text-xs font-mono whitespace-pre-wrap">
                    {JSON.stringify(kernelData.section_11_dasha_catalogue, null, 2)}
                  </pre>
                </div>
              </DashboardCard>
            )}
            
            {/* Varsaphala & Transit - Section 12 */}
            {kernelData.section_12_varsaphala_transit && (
              <DashboardCard
                id="varsaphala_transit"
                title="Section 12: Varsaphala & Transit"
                icon={Sunrise}
                expanded={expandedSections.varsaphala_transit}
                onToggle={() => toggleSection('varsaphala_transit')}
              >
                <div className="bg-black/20 rounded-xl p-4 overflow-auto max-h-[500px]">
                  <pre className="text-rose-300 text-xs font-mono whitespace-pre-wrap">
                    {JSON.stringify(kernelData.section_12_varsaphala_transit, null, 2)}
                  </pre>
                </div>
              </DashboardCard>
            )}

            {/* Aspects */}
            {kernelData.aspects && kernelData.aspects.length > 0 && (
              <DashboardCard
                id="aspects"
                title="Planetary Aspects"
                icon={TrendingUp}
                expanded={expandedSections.aspects}
                onToggle={() => toggleSection('aspects')}
              >
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {kernelData.aspects.map((aspect, idx) => (
                    <div key={idx} className="bg-white/5 rounded-lg p-3 backdrop-blur-sm flex items-center gap-3">
                      <span className="text-white/80 text-sm">{aspect.from}</span>
                      <div className="flex-1 h-px bg-gradient-to-r from-cyan-500/50 to-blue-500/50" />
                      <span className="text-cyan-400 text-xs">{aspect.type}</span>
                      <div className="flex-1 h-px bg-gradient-to-r from-blue-500/50 to-purple-500/50" />
                      <span className="text-white/80 text-sm">{aspect.to}</span>
                    </div>
                  ))}
                </div>
              </DashboardCard>
            )}

            {/* Dashas */}
            {kernelData.dashas && (
              <DashboardCard
                id="dashas"
                title="Vimshottari Dasha Periods"
                icon={Calendar}
                expanded={expandedSections.dashas}
                onToggle={() => toggleSection('dashas')}
              >
                <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-2xl p-6 border border-purple-500/20 mb-4">
                  <p className="text-white/60 text-sm mb-2">Current Mahadasha</p>
                  <p className="text-white text-3xl font-light">{kernelData.dashas.current_dasha}</p>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                  {kernelData.dashas.sequence && kernelData.dashas.sequence.map((dasha, idx) => (
                    <div key={idx} className="bg-white/5 rounded-xl p-3 backdrop-blur-sm text-center">
                      <p className="text-white text-sm font-medium mb-1">{dasha.lord}</p>
                      <p className="text-cyan-400 text-xs">{dasha.years} years</p>
                    </div>
                  ))}
                </div>
              </DashboardCard>
            )}

            {/* Yogas */}
            {kernelData.yogas && kernelData.yogas.length > 0 && (
              <DashboardCard
                id="yogas"
                title="Yogas (Planetary Combinations)"
                icon={Sparkles}
                expanded={expandedSections.yogas}
                onToggle={() => toggleSection('yogas')}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {kernelData.yogas.map((yoga, idx) => (
                    <div key={idx} className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-xl p-4 border border-amber-500/20">
                      <h4 className="text-white text-sm font-medium mb-2">{yoga.name}</h4>
                      <p className="text-cyan-400 text-xs capitalize">Strength: {yoga.strength}</p>
                    </div>
                  ))}
                </div>
              </DashboardCard>
            )}

            {/* Strengths */}
            {kernelData.strengths && (
              <DashboardCard
                id="strengths"
                title="Planetary Strengths"
                icon={Award}
                expanded={expandedSections.strengths}
                onToggle={() => toggleSection('strengths')}
              >
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {Object.entries(kernelData.strengths).map(([planet, data]) => (
                    <div key={planet} className="bg-white/5 rounded-xl p-4 backdrop-blur-sm">
                      <p className="text-white text-sm font-medium mb-2">{planet}</p>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex-1 bg-white/10 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-cyan-400 to-blue-500 h-2 rounded-full transition-all"
                            style={{ width: `${data.score}%` }}
                          />
                        </div>
                        <span className="text-cyan-400 text-xs">{data.score}</span>
                      </div>
                      <p className={`text-xs ${
                        data.rating === 'strong' ? 'text-green-400' : 
                        data.rating === 'moderate' ? 'text-yellow-400' : 'text-orange-400'
                      }`}>
                        {data.rating}
                      </p>
                    </div>
                  ))}
                </div>
              </DashboardCard>
            )}

            {/* Transits */}
            {kernelData.transits && (
              <DashboardCard
                id="transits"
                title="Current Transits"
                icon={Plane}
                expanded={expandedSections.transits}
                onToggle={() => toggleSection('transits')}
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-blue-500/10 to-indigo-500/10 rounded-xl p-4 border border-blue-500/20">
                    <p className="text-white/60 text-xs mb-2">Jupiter Transit</p>
                    <p className="text-white text-lg font-medium">{kernelData.transits.jupiter?.sign || 'N/A'}</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl p-4 border border-purple-500/20">
                    <p className="text-white/60 text-xs mb-2">Saturn Transit</p>
                    <p className="text-white text-lg font-medium">{kernelData.transits.saturn?.sign || 'N/A'}</p>
                  </div>
                  <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-xl p-4 border border-orange-500/20">
                    <p className="text-white/60 text-xs mb-2">Rahu Transit</p>
                    <p className="text-white text-lg font-medium">{kernelData.transits.rahu?.sign || 'N/A'}</p>
                  </div>
                </div>
              </DashboardCard>
            )}

            {/* Timeline */}
            {kernelData.timeline && (
              <DashboardCard
                id="timeline"
                title="Life Timeline"
                icon={Sunrise}
                expanded={expandedSections.timeline}
                onToggle={() => toggleSection('timeline')}
              >
                <div className="bg-white/5 rounded-2xl p-6 backdrop-blur-sm">
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <p className="text-white/60 text-sm">Birth Year</p>
                      <p className="text-white text-2xl font-light">{kernelData.timeline.birth_year}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm">Current Age</p>
                      <p className="text-cyan-400 text-2xl font-medium">{kernelData.timeline.current_age}</p>
                    </div>
                  </div>
                  {kernelData.timeline.major_periods && (
                    <div className="space-y-3">
                      {kernelData.timeline.major_periods.map((period, idx) => (
                        <div key={idx} className="flex items-center gap-4 bg-white/5 rounded-lg p-3">
                          <div className="text-cyan-400 text-sm font-medium min-w-[100px]">{period.age_range}</div>
                          <div className="flex-1 h-2 bg-white/10 rounded-full">
                            <div className="h-2 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full" style={{ width: '100%' }} />
                          </div>
                          <div className="text-white/80 text-sm capitalize min-w-[100px] text-right">{period.phase}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </DashboardCard>
            )}

            {/* Edit Birth Details Button */}
            <div className="text-center mt-12">
              <button
                onClick={() => {
                  setShowForm(true);
                  setKernelData(null);
                  setShowVideo(false);
                }}
                className="px-8 py-3 bg-white/10 border border-white/20 rounded-xl text-white hover:bg-white/20 transition-colors"
              >
                Edit Birth Details
              </button>
            </div>

          </div>
        )}

      </div>
    </CosmicBackground>
  );
};

// Dashboard Card Component with Expand/Collapse
const DashboardCard = ({ id, title, icon: Icon, children, expanded, onToggle }) => (
  <div className="mb-6">
    <div 
      onClick={onToggle}
      className="flex items-center justify-between cursor-pointer bg-gradient-to-r from-cyan-500/10 to-blue-500/10 backdrop-blur-sm rounded-t-2xl p-4 border border-white/10 hover:border-cyan-500/30 transition-all"
    >
      <div className="flex items-center gap-3">
        <Icon className="w-6 h-6 text-cyan-400" />
        <h3 className="text-white text-xl font-light tracking-wide">{title}</h3>
      </div>
      {expanded ? (
        <ChevronUp className="w-5 h-5 text-cyan-400" />
      ) : (
        <ChevronDown className="w-5 h-5 text-cyan-400" />
      )}
    </div>
    
    {expanded && (
      <div className="bg-white/5 backdrop-blur-sm rounded-b-2xl p-6 border border-t-0 border-white/10">
        {children}
      </div>
    )}
  </div>
);

// Info Card Component
const InfoCard = ({ label, value }) => (
  <div className="bg-white/5 rounded-xl p-3 backdrop-blur-sm">
    <p className="text-white/50 text-xs mb-1">{label}</p>
    <p className="text-white text-sm font-medium">{value}</p>
  </div>
);

// Planet Card Component
const PlanetCard = ({ planet }) => (
  <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10 hover:border-cyan-500/30 transition-all">
    <div className="flex items-start justify-between mb-3">
      <h3 className="text-white text-base font-medium">{planet.Planet}</h3>
      {planet['Retro(R)'] === 'Retro' && (
        <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">R</span>
      )}
    </div>
    
    <div className="space-y-2 text-sm">
      <InfoRow label="Sign" value={planet.Sign} />
      <InfoRow label="Nakshatra" value={planet.Nakshatra} />
      <InfoRow label="House" value={planet.House} />
      <InfoRow label="Degree" value={planet.Degree} />
      {planet.Status && planet.Status !== '—' && planet.Status !== '--' && (
        <InfoRow label="Status" value={planet.Status} highlight />
      )}
    </div>
  </div>
);

// Info Row Component
const InfoRow = ({ label, value, highlight }) => (
  <div className="flex justify-between items-center">
    <span className="text-white/50 text-xs">{label}</span>
    <span className={`text-xs ${highlight ? 'text-cyan-400 font-medium' : 'text-white/80'}`}>
      {value}
    </span>
  </div>
);

export default KernelDisplay;
